import { effect, inject, Injectable } from "@angular/core";
import { ListRange } from "@angular/cdk/collections";
import { CdkVirtualScrollViewport, VirtualScrollStrategy } from "@angular/cdk/scrolling";
import { Subject } from "rxjs";
import { ColumnStateService } from "./column-state.service";

export const COLUMN_WIDTHS = {
    expanded: 308,
    collapsed: 50,
    gap: 10,
} as const;

const RANGE_BUFFER_PX = {
    min: 200,
    max: 450,
} as const;

const BOUNDARY_EPSILON_PX = 10;

/**
 * Threshold for switching between virtual scroll and flex layout.
 * Virtual scroll is complex and has performance overhead; under
 * 20 columns a simple flex layout with CDK scrollable works better.
 */
export const VIRTUAL_SCROLL_THRESHOLD = 20;

/**
 * Custom horizontal virtual scroll strategy that accounts for variable column widths
 * (columns can be individually expanded at 308px or collapsed at 50px).
 *
 * Provided per AssigneesColumnsWrapperComponent instance alongside ColumnStateService.
 * The wrapper sets column IDs via setColumnIds(); the strategy reads expansion state
 * reactively from ColumnStateService to keep layout in sync whenever any column toggles.
 */
@Injectable()
export class AssigneesColumnScrollStrategy implements VirtualScrollStrategy {
    private readonly columnStateService = inject(ColumnStateService);

    private viewport: CdkVirtualScrollViewport | null = null;
    private columnIds: string[] = [];
    private currentWidths: number[] = [];
    private offsets: number[] = []; // cumulative start offset for each item
    private lastRenderedOffset = -1;

    readonly scrolledIndexChange = new Subject<number>();

    constructor() {
        // Re-layout whenever any column's expanded state changes.
        effect(() => {
            this.columnStateService.getStates(); // tracked
            this.refreshLayout();
        });
    }

    // ── VirtualScrollStrategy interface ──────────────────────────────────────

    attach(viewport: CdkVirtualScrollViewport): void {
        this.viewport = viewport;
        this.refreshLayout();
    }

    detach(): void {
        this.viewport = null;
    }

    onContentScrolled(): void {
        this.updateRenderedRange();
    }

    onDataLengthChanged(): void {
        this.refreshLayout();
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    // Required by VirtualScrollStrategy interface; no-op implementation.
    onContentRendered(): void {}

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    // Required by VirtualScrollStrategy interface; no-op implementation.
    onRenderedOffsetChanged(): void {}

    scrollToIndex(index: number, behavior: ScrollBehavior): void {
        if (this.viewport && index >= 0 && index < this.offsets.length) {
            this.viewport.scrollToOffset(this.offsets[index], behavior);
        }
    }

    // ── Public API ────────────────────────────────────────────────────────────

    /**
     * Called by AssigneesColumnsWrapperComponent whenever the column list changes.
     * Column IDs must match the keys used in ColumnStateService.
     */
    setColumnIds(ids: string[]): void {
        this.columnIds = ids;
        this.refreshLayout();
    }

    // ── Private helpers ───────────────────────────────────────────────────────

    private getWidths(): number[] {
        const states = this.columnStateService.getStates();
        return this.columnIds.map((id) => ((states.get(id) ?? true) ? COLUMN_WIDTHS.expanded : COLUMN_WIDTHS.collapsed));
    }

    private refreshLayout(): void {
        if (!this.viewport) return;
        const widths = this.getWidths();
        this.currentWidths = widths;
        this.computeOffsets(widths);
        this.updateTotalContentSize(widths);
        this.lastRenderedOffset = -1;
        this.updateRenderedRange();
    }

    private computeOffsets(widths: number[]): void {
        this.offsets = [];
        let cumulative = 0;
        for (const w of widths) {
            this.offsets.push(cumulative);
            cumulative += w + COLUMN_WIDTHS.gap;
        }
    }

    private updateTotalContentSize(widths: number[]): void {
        const total = this.getTotalContentSize(widths);
        this.viewport!.setTotalContentSize(total);
    }

    private getTotalContentSize(widths: number[]): number {
        return widths.length ? widths.reduce((sum, width) => sum + width, 0) + COLUMN_WIDTHS.gap * (widths.length - 1) : 0;
    }

    private updateRenderedRange(): void {
        if (!this.viewport || !this.offsets.length) return;

        const scrollOffset = this.getClampedScrollOffset();
        const viewportSize = this.viewport.getViewportSize();
        const widths = this.currentWidths;
        const viewportEnd = scrollOffset + viewportSize;
        const currentRange = this.viewport.getRenderedRange();

        // Keep the current rendered window while both sides still have enough pixel buffer.
        if (currentRange.end > currentRange.start) {
            const renderedStart = currentRange.start;
            const renderedEnd = Math.min(this.offsets.length - 1, currentRange.end - 1);
            const renderedStartOffset = this.offsets[renderedStart] ?? 0;
            const renderedEndOffset = (this.offsets[renderedEnd] ?? 0) + (widths[renderedEnd] ?? 0);

            const startBuffer = scrollOffset - renderedStartOffset;
            const endBuffer = renderedEndOffset - viewportEnd;

            if (startBuffer >= RANGE_BUFFER_PX.min && endBuffer >= RANGE_BUFFER_PX.min) {
                return;
            }
        }

        const start = this.findStartIndex(Math.max(0, scrollOffset - RANGE_BUFFER_PX.max), widths);
        const end = this.findEndIndex(viewportEnd + RANGE_BUFFER_PX.max);

        // ListRange.end is exclusive
        const range: ListRange = { start, end: end + 1 };
        if (currentRange.start !== range.start || currentRange.end !== range.end) {
            this.viewport.setRenderedRange(range);
        }

        const renderedOffset = this.offsets[start] ?? 0;
        if (this.lastRenderedOffset !== renderedOffset) {
            this.viewport.setRenderedContentOffset(renderedOffset);
            this.lastRenderedOffset = renderedOffset;
        }

        this.scrolledIndexChange.next(start);
    }

    private getClampedScrollOffset(): number {
        if (!this.viewport) return 0;

        const rawScrollOffset = Math.max(0, this.viewport.measureScrollOffset());
        const viewportSize = this.viewport.getViewportSize();
        const totalContentSize = this.getTotalContentSize(this.currentWidths);
        const maxScrollOffset = Math.max(0, totalContentSize - viewportSize);

        return Math.min(rawScrollOffset, maxScrollOffset);
    }

    private findStartIndex(scrollOffset: number, widths: number[]): number {
        let low = 0;
        let high = this.offsets.length - 1;

        while (low < high) {
            const mid = Math.floor((low + high) / 2);
            const rightEdge = this.offsets[mid] + widths[mid];
            if (rightEdge <= scrollOffset - BOUNDARY_EPSILON_PX) {
                low = mid + 1;
            } else {
                high = mid;
            }
        }

        return low;
    }

    private findEndIndex(viewportEnd: number): number {
        let low = 0;
        let high = this.offsets.length - 1;

        while (low < high) {
            const mid = Math.floor((low + high + 1) / 2);
            if (this.offsets[mid] < viewportEnd + BOUNDARY_EPSILON_PX) {
                low = mid;
            } else {
                high = mid - 1;
            }
        }

        return low;
    }
}
